import type { CanvasNote, Project, Task, TimeEntry } from '@/src/renderer/lib/db/models'

/*
  **Suche** (#233, CONTEXT.md → Suche): Wer passt, und in welcher Reihenfolge. Reine Funktionen —
  die Datenquellen filtern damit, das Overlay hebt damit hervor. Kein Fuzzy-Matching: Die Stufen
  unten sind vorhersagbarer, und man findet, was man getippt hat.
*/

/**
 * 0: Titel beginnt mit dem ersten Wort · 1: jedes Wort beginnt ein Wort im Titel ·
 * 2: alle Wörter stecken im Titel · 3: erst mit der Beschreibung.
 */
export type SearchStage = 0 | 1 | 2 | 3

export interface SearchOptions {
  /** Erledigte Aufgaben und nicht mehr aktive Projekte (samt ihrer Pinnwand) mit aufnehmen. */
  includeCompleted?: boolean
  /** Dessen Treffer stehen vorn; die anderen Lebensbereiche folgen. */
  activeLifeSpace?: string
  limit?: number
}

/** Klein, ohne Akzente, `ß` als `ss` — „gießen“ findet „Giessen“ und „Gießen“. */
export function normalizeSearchText(text: string | null | undefined): string {
  return (text ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss')
}

export function searchWords(query: string): string[] {
  return normalizeSearchText(query).split(/\s+/).filter(Boolean)
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' }

/** Klartext aus dem HTML eines Textes (ADR 0029) — gesucht und gezeigt wird ohne Tags. */
export function plainText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, name: string) => ENTITIES[name] ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Mehrere Wörter müssen ALLE vorkommen, im Titel oder in der Beschreibung. */
export function rankText(title: string | null | undefined, body: string | null | undefined, words: readonly string[]): SearchStage | null {
  if (words.length === 0) return null
  const t = normalizeSearchText(title)
  const b = normalizeSearchText(body)
  if (!words.every(w => t.includes(w) || b.includes(w))) return null
  if (!words.every(w => t.includes(w))) return 3
  if (t.startsWith(words[0]!)) return 0
  const titleWords = t.split(/[^\p{L}\p{N}]+/u)
  if (words.every(w => titleWords.some(x => x.startsWith(w)))) return 1
  return 2
}

export const rankTask = (task: Task, words: readonly string[]) => rankText(task.title, task.description, words)

export const rankProject = (project: Project, words: readonly string[]) => rankText(project.name, project.description, words)

/**
 * Titel zählt wie ein Aufgabentitel, der Inhalt wie eine Beschreibung. Ohne Titel IST der Anfang
 * des Inhalts der Titel (so steht er auch in der Trefferzeile). Ein Bereich hat nur seinen Titel.
 */
export function rankCanvasNote(note: CanvasNote, words: readonly string[]): SearchStage | null {
  if (note.kind === 'area') return rankText(note.title, '', words)
  const content = plainText(note.content)
  return note.title?.trim() ? rankText(note.title, content, words) : rankText(content, '', words)
}

/** Wie eine Pinnwand-Zeile heißt: ihr Titel, sonst der Anfang des Textes. */
export function canvasNoteHeadline(note: CanvasNote): string {
  return note.title?.trim() || plainText(note.content).slice(0, 80)
}

export const isOpenTask = (task: Task) => task.status !== 'completed' && task.status !== 'cancelled'

/** Erledigt gilt für Projekte, die nicht mehr aktiv sind — abgeschlossen oder archiviert. */
export const isActiveProject = (project: Project) => project.status === 'active'

interface Ranked<T> { item: T; lifeSpace: string; stage: SearchStage; updatedAt: number }

function rankAndCut<T>(ranked: Ranked<T>[], { activeLifeSpace, limit }: SearchOptions): T[] {
  const front = (r: Ranked<T>) => (r.lifeSpace === activeLifeSpace ? 0 : 1)
  return ranked
    .sort((a, b) => front(a) - front(b) || a.stage - b.stage || b.updatedAt - a.updatedAt)
    .slice(0, limit ?? Infinity)
    .map(r => r.item)
}

const time = (date: Date | string | null | undefined) => (date ? new Date(date).getTime() : 0)

/** Aufgaben (auch Unteraufgaben) über alle Lebensbereiche; gelöschte nie, erledigte nur mit Schalter. */
export function searchTaskList(tasks: readonly Task[], query: string, options: SearchOptions = {}): Task[] {
  const words = searchWords(query)
  if (words.length === 0) return []
  const ranked: Ranked<Task>[] = []
  for (const task of tasks) {
    if (task.deletedAt || (!options.includeCompleted && !isOpenTask(task))) continue
    const stage = rankTask(task, words)
    if (stage !== null) ranked.push({ item: task, lifeSpace: task.lifeSpace, stage, updatedAt: time(task.updatedAt) })
  }
  return rankAndCut(ranked, options)
}

/** Projekte ohne System-Projekte und gelöschte; nicht mehr aktive nur mit Schalter. */
export function searchProjectList(projects: readonly Project[], query: string, options: SearchOptions = {}): Project[] {
  const words = searchWords(query)
  if (words.length === 0) return []
  const ranked: Ranked<Project>[] = []
  for (const project of projects) {
    if (project.deletedAt || project.isSystem || (!options.includeCompleted && !isActiveProject(project))) continue
    const stage = rankProject(project, words)
    if (stage !== null) ranked.push({ item: project, lifeSpace: project.lifeSpace, stage, updatedAt: time(project.updatedAt) })
  }
  return rankAndCut(ranked, options)
}

/**
 * Notiz, Memo, Text und Bereich der Pinnwand. Der Lebensbereich kommt vom Projekt; ohne lebendes
 * Projekt fällt das Element heraus, mit einem nicht mehr aktiven nur ohne Schalter.
 */
export function searchCanvasNoteList(
  notes: readonly CanvasNote[],
  projectsById: ReadonlyMap<string, Project>,
  query: string,
  options: SearchOptions = {},
): CanvasNote[] {
  const words = searchWords(query)
  if (words.length === 0) return []
  const ranked: Ranked<CanvasNote>[] = []
  for (const note of notes) {
    const project = projectsById.get(note.projectId)
    if (note.deletedAt || !project || project.deletedAt || project.isSystem) continue
    if (!options.includeCompleted && !isActiveProject(project)) continue
    const stage = rankCanvasNote(note, words)
    if (stage !== null) ranked.push({ item: note, lifeSpace: project.lifeSpace, stage, updatedAt: time(note.updatedAt) })
  }
  return rankAndCut(ranked, options)
}

/*
  **Vor dem Tippen** zeigt eine einzelne Trefferart ihre zuletzt geänderten Einträge (gewünscht
  02.10.2026) — dieselben Regeln wie beim Suchen, nur ohne Suchwort und nach Zeit allein: Der
  Lebensbereich zählt hier nicht, wie bei „Zuletzt geöffnet“.
*/
type LatestOptions = Pick<SearchOptions, 'includeCompleted' | 'limit'>

const latest = <T extends { updatedAt: Date | string }>(items: T[], limit?: number) =>
  items.sort((a, b) => time(b.updatedAt) - time(a.updatedAt)).slice(0, limit ?? Infinity)

export function latestTaskList(tasks: readonly Task[], { includeCompleted, limit }: LatestOptions = {}): Task[] {
  return latest(tasks.filter(task => !task.deletedAt && (includeCompleted || isOpenTask(task))), limit)
}

export function latestProjectList(projects: readonly Project[], { includeCompleted, limit }: LatestOptions = {}): Project[] {
  return latest(projects.filter(project => !project.deletedAt && !project.isSystem && (includeCompleted || isActiveProject(project))), limit)
}

export function latestCanvasNoteList(
  notes: readonly CanvasNote[],
  projectsById: ReadonlyMap<string, Project>,
  { includeCompleted, limit }: LatestOptions = {},
): CanvasNote[] {
  return latest(notes.filter((note) => {
    const project = projectsById.get(note.projectId)
    // Leere Zettel (und Bereiche ohne Titel) sagen in der Liste nichts (gewünscht 02.10.2026).
    if (!canvasNoteHeadline(note)) return false
    return !note.deletedAt && !!project && !project.deletedAt && !project.isSystem && (includeCompleted || isActiveProject(project))
  }), limit)
}

/**
 * **Zuletzt im Fokus**: die Aufgaben nach ihrem jüngsten Fokus-Start, jede einmal. Gelöschte
 * Abschnitte und Aufgaben fallen weg, erledigte nur mit Schalter.
 */
export function latestFocusedTaskList(
  entries: readonly TimeEntry[],
  tasksById: ReadonlyMap<string, Task>,
  { includeCompleted, limit }: LatestOptions = {},
): Task[] {
  const seen = new Set<string>()
  const result: Task[] = []
  for (const entry of [...entries].sort((a, b) => time(b.startTime) - time(a.startTime))) {
    if (result.length === limit) break
    if (entry.deletedAt || seen.has(entry.taskId)) continue
    seen.add(entry.taskId)
    const task = tasksById.get(entry.taskId)
    if (task && !task.deletedAt && (includeCompleted || isOpenTask(task))) result.push(task)
  }
  return result
}

/** Stücke eines Textes, die getroffenen markiert — für `<mark>` in der Trefferzeile. */
export function highlightParts(text: string, words: readonly string[]): Array<{ text: string; hit: boolean }> {
  if (!text || words.length === 0) return [{ text, hit: false }]
  // Normalisieren kann Zeichen vermehren (ß → ss): je Zeichen der Suchfassung merken, woher es kommt.
  let normalized = ''
  const origin: number[] = []
  Array.from(text).forEach((char, index) => {
    for (const c of normalizeSearchText(char)) {
      normalized += c
      origin.push(index)
    }
  })
  const chars = Array.from(text)
  const marked = new Array<boolean>(chars.length).fill(false)
  for (const word of words) {
    let from = 0
    let at: number
    while ((at = normalized.indexOf(word, from)) !== -1) {
      for (let k = at; k < at + word.length; k++) marked[origin[k]!] = true
      from = at + word.length
    }
  }
  const parts: Array<{ text: string; hit: boolean }> = []
  chars.forEach((char, i) => {
    const last = parts[parts.length - 1]
    if (last && last.hit === marked[i]) last.text += char
    else parts.push({ text: char, hit: marked[i]! })
  })
  return parts
}

/** Ein Ausschnitt um den ersten Treffer im Text — für Treffer, die nur in der Beschreibung liegen. */
export function searchExcerpt(text: string, words: readonly string[], length = 80): string {
  const plain = plainText(text)
  const normalized = normalizeSearchText(plain)
  let at = -1
  for (const word of words) {
    const i = normalized.indexOf(word)
    if (i !== -1 && (at === -1 || i < at)) at = i
  }
  if (at <= 24) return plain.slice(0, length)
  // Die Stelle stimmt nur ungefähr, wenn Normalisieren Zeichen vermehrt hat — für einen Ausschnitt genügt das.
  const from = at - 24
  return `…${plain.slice(from, from + length)}`
}
